import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Stack,
  Autocomplete,
  Divider,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useUser } from "../../Context/UserContext";
import DynamicTable from "../Dynamic/DynamicTable";
import DynamicOffcanvas from "../Dynamic/DynamicOffcanvas";
import { toast } from "react-toastify";
import { GetApi, PostApi, DeleteApi } from "../../Services/api/ApiCall";
import API_URLS from "../../Services/api/action";

const canModify = (role) => role === "department_admin";

const now = new Date();
const baseYear =
  now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
const currentFY = `${baseYear}-${baseYear + 1}`;
const years = Array.from(
  { length: 10 },
  (_, i) => `${baseYear + i}-${baseYear + i + 1}`,
);

// ✅ REUSABLE GREEN STYLING OBJECT
const greenInputSx = {
  "& .MuiOutlinedInput-root": {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#4caf50",
      borderWidth: 1.5,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2e7d32 !important",
      borderWidth: 2,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1b5e20",
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root": {
    color: "#000000",
    fontWeight: 500,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#000000",
  },
};

const DistrictTarget = () => {
  const { token, user, role, authData, userId } = useUser();

  // Data States
  const [data, setData] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [deptTargetLimits, setDeptTargetLimits] = useState([]);

  // UI States
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [userDepartmentName, setUserDepartmentName] = useState("Loading...");

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [existModal, setExistModal] = useState(false);
  const [existInfo, setExistInfo] = useState(null);

  // Detailed pending items for outside view
  const [overallBalance, setOverallBalance] = useState(0);
  const [pendingDetails, setPendingDetails] = useState([]);

  // Filter States
  const [filterDepartment, setFilterDepartment] = useState(null);
  const [filterDistrict, setFilterDistrict] = useState(null);
  const [filterScheme, setFilterScheme] = useState(null);

  const [form, setForm] = useState({
    district_id: null,
    financial_year: currentFY,
    scheme_type: null,
    scheme_id: null,
    target_quantity: "",
  });

  const [errors, setErrors] = useState({});
  const hasModifyPermission = canModify(role);

  // ================= 1. FETCH DATA =================
  const fetchTargets = async () => {
    try {
      const res = await GetApi(
        `${API_URLS.DistrictTarget}?user_id=${userId}`,
        token,
      );
      if (res && res.success) {
        const freshData = res.data || [];
        setData(freshData);
        return freshData;
      }
      setData([]);
      return [];
    } catch (err) {
      console.error("Fetch Targets Error:", err);
      setData([]);
      return [];
    }
  };

  const fetchInitialData = async () => {
    try {
      const [distRes, schemeRes, deptRes, deptLimitsRes] = await Promise.all([
        GetApi(API_URLS.GetDistricts, token),
        GetApi(API_URLS.Schemes, token),
        GetApi(API_URLS.Department, token),
        GetApi(`${API_URLS.DepartmentTarget}?user_id=${userId}`, token),
      ]);

      // ✅ SORT DISTRICTS ALPHABETICALLY FOR DROPDOWNS
      const rawDistricts = distRes?.data || distRes || [];
      const sortedDistricts = rawDistricts.sort((a, b) =>
        (a.District_Name || "").localeCompare(b.District_Name || ""),
      );
      setDistricts(sortedDistricts);

      setSchemes(schemeRes?.data || schemeRes || []);
      setDepartments(deptRes?.data || deptRes || []);

      const deptList = deptRes?.data || deptRes || [];
      const myDept = deptList.find(
        (d) => Number(d.id) === Number(authData?.department_id),
      );
      setUserDepartmentName(myDept?.name || "My Department");

      const limits = deptLimitsRes?.data || deptLimitsRes || [];
      setDeptTargetLimits(limits);

      calculateOverallBalance(limits, [], form.financial_year);
    } catch (err) {
      console.error("Setup Error:", err);
    }
  };

  const calculateOverallBalance = (
    currentLimits = deptTargetLimits,
    currentData = data,
    selectedYearStr = currentFY,
  ) => {
    let totalBalance = 0;
    const details = [];

    const selectedYear = Number(selectedYearStr.split("-")[0]);

    currentLimits.forEach((lim) => {
      const dbUsed = currentData.reduce((sum, row) => {
        if (!row.start_date) return sum;
        const rowYear = new Date(row.start_date).getFullYear();
        if (rowYear !== selectedYear) return sum;

        if (lim.scheme_type === "Scheme") {
          if (Number(row.scheme_id) === Number(lim.scheme_id))
            return sum + Number(row.target_quantity || 0);
        } else {
          if (row.scheme_type === "Non-Scheme")
            return sum + Number(row.target_quantity || 0);
        }
        return sum;
      }, 0);

      const balance = Number(lim.target_quantity) - dbUsed;

      if (balance !== 0) {
        totalBalance += balance;
        details.push({
          id: lim.id || lim.target_id,
          department_name: lim.department_name || userDepartmentName,
          scheme_name:
            lim.scheme_type === "Scheme" ? lim.scheme_name : "Non-Scheme",
          balance: balance,
          isOverAssigned: balance < 0,
        });
      }
    });

    setOverallBalance(totalBalance);
    setPendingDetails(details);
  };

  useEffect(() => {
    if (token && userId) {
      fetchTargets().then((freshData) => {
        if (deptTargetLimits.length > 0) {
          calculateOverallBalance(
            deptTargetLimits,
            freshData,
            form.financial_year,
          );
        }
      });
      fetchInitialData();
    }
  }, [token, userId]);

  useEffect(() => {
    calculateOverallBalance(deptTargetLimits, data, form.financial_year);
  }, [data, deptTargetLimits, form.financial_year]);

  // ================= 2. DEPARTMENT RESTRICTION LOGIC =================
  const displayedDeptLimits = useMemo(() => {
    if (!form.financial_year) return [];
    return deptTargetLimits.filter(
      (lim) => lim.financial_year === form.financial_year,
    );
  }, [deptTargetLimits, form.financial_year]);

  const deptAllowedTypes = useMemo(() => {
    const types = new Set(displayedDeptLimits.map((l) => l.scheme_type));
    if (types.has("Non-Scheme") && types.has("Scheme"))
      return ["Non-Scheme", "Scheme"];
    if (types.has("Non-Scheme")) return ["Non-Scheme"];
    if (types.has("Scheme")) return ["Scheme"];
    return [];
  }, [displayedDeptLimits]);

  const deptAllowedSchemeIds = useMemo(() => {
    return displayedDeptLimits
      .filter((l) => l.scheme_type === "Scheme")
      .map((l) => Number(l.scheme_id));
  }, [displayedDeptLimits]);

  // ================= 3. DISTRICT HIERARCHY LOGIC =================
  const getExistingForDistYear = (
    targetData,
    distId,
    fy,
    excludeEditId = null,
  ) => {
    return targetData.filter((item) => {
      const isEditItem =
        excludeEditId !== null &&
        (String(item.id) === String(excludeEditId) ||
          String(item.target_id) === String(excludeEditId));
      const itemYear = item.start_date
        ? `${new Date(item.start_date).getFullYear()}-${new Date(item.start_date).getFullYear() + 1}`
        : null;
      return (
        Number(item.district_id) === Number(distId) &&
        itemYear === fy &&
        !isEditItem
      );
    });
  };

  const isDistrictComplete = (distId, fy, excludeEditId = null) => {
    if (deptAllowedTypes.length === 0) return true;
    const existing = getExistingForDistYear(data, distId, fy, excludeEditId);

    const hasNonScheme = existing.some((t) => t.scheme_type === "Non-Scheme");
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));

    const nonSchemeDone = deptAllowedTypes.includes("Non-Scheme")
      ? hasNonScheme
      : true;
    const schemesDone = deptAllowedTypes.includes("Scheme")
      ? deptAllowedSchemeIds.every((s) => usedSchemeIds.includes(s))
      : true;

    return nonSchemeDone && schemesDone;
  };

  const availableDistricts = useMemo(() => {
    if (!form.financial_year) return districts;
    return districts.filter(
      (dist) => !isDistrictComplete(dist.id, form.financial_year, editId),
    );
  }, [
    data,
    form.financial_year,
    districts,
    deptAllowedTypes,
    deptAllowedSchemeIds,
    editId,
  ]);

  const availableTypes = useMemo(() => {
    if (
      deptAllowedTypes.length === 0 ||
      !form.district_id ||
      !form.financial_year
    )
      return [];
    const existing = getExistingForDistYear(
      data,
      form.district_id,
      form.financial_year,
      editId,
    );

    const hasNonScheme = existing.some((t) => t.scheme_type === "Non-Scheme");
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));
    const hasRemainingSchemes = deptAllowedSchemeIds.some(
      (s) => !usedSchemeIds.includes(s),
    );

    const finalTypes = [];
    if (deptAllowedTypes.includes("Non-Scheme") && !hasNonScheme)
      finalTypes.push("Non-Scheme");
    if (deptAllowedTypes.includes("Scheme") && hasRemainingSchemes)
      finalTypes.push("Scheme");
    return finalTypes;
  }, [
    data,
    form.district_id,
    form.financial_year,
    editId,
    deptAllowedTypes,
    deptAllowedSchemeIds,
  ]);

  const availableSchemes = useMemo(() => {
    if (
      form.scheme_type !== "Scheme" ||
      !form.district_id ||
      !form.financial_year
    )
      return [];
    const existing = getExistingForDistYear(
      data,
      form.district_id,
      form.financial_year,
      editId,
    );
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));

    return schemes.filter(
      (s) =>
        deptAllowedSchemeIds.includes(Number(s.id)) &&
        !usedSchemeIds.includes(Number(s.id)),
    );
  }, [
    data,
    form.district_id,
    form.financial_year,
    editId,
    form.scheme_type,
    schemes,
    deptAllowedSchemeIds,
  ]);

  const hierarchyStatus = useMemo(() => {
    if (!form.district_id || !form.financial_year) return null;
    const existing = getExistingForDistYear(
      data,
      form.district_id,
      form.financial_year,
      null,
    );
    const hasNonScheme = existing.some((t) => t.scheme_type === "Non-Scheme");
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));
    return {
      nonSchemeSet: hasNonScheme,
      schemesSet: usedSchemeIds.length,
      totalSchemes: deptAllowedSchemeIds.length,
      remainingSchemes: deptAllowedSchemeIds.length - usedSchemeIds.length,
    };
  }, [form.district_id, form.financial_year, data, deptAllowedSchemeIds]);

  useEffect(() => {
    if (form.scheme_type && !availableTypes.includes(form.scheme_type)) {
      setForm((prev) => ({ ...prev, scheme_type: null, scheme_id: null }));
    }
  }, [availableTypes, form.scheme_type]);

  useEffect(() => {
    if (form.scheme_type === "Scheme" && form.scheme_id) {
      if (
        !availableSchemes.some((s) => Number(s.id) === Number(form.scheme_id))
      ) {
        setForm((prev) => ({ ...prev, scheme_id: null }));
      }
    }
  }, [availableSchemes, form.scheme_type, form.scheme_id]);

  useEffect(() => {
    if (
      form.district_id &&
      !availableDistricts.some((d) => d.id === form.district_id)
    ) {
      setForm((prev) => ({
        ...prev,
        district_id: null,
        scheme_type: null,
        scheme_id: null,
      }));
    }
  }, [availableDistricts, form.district_id]);

  // ================= 4. LIMIT CALCULATION =================
  const getMaxAllowed = () => {
    if (!form.financial_year || !form.scheme_type) return null;
    const isScheme = form.scheme_type === "Scheme";
    if (isScheme && !form.scheme_id) return null;

    let totalAllowed = 0;
    if (isScheme) {
      const masterLimit = deptTargetLimits.find(
        (lim) =>
          Number(lim.scheme_id) === Number(form.scheme_id) &&
          lim.financial_year === form.financial_year,
      );
      totalAllowed = Number(masterLimit?.target_quantity || 0);
    } else {
      const masterLimit = deptTargetLimits.find(
        (lim) =>
          lim.scheme_type === "Non-Scheme" &&
          lim.financial_year === form.financial_year,
      );
      totalAllowed = Number(masterLimit?.target_quantity || 0);
    }

    const selectedYear = Number(form.financial_year.split("-")[0]);
    const dbUsed = data.reduce((sum, row) => {
      if (!row.start_date) return sum;
      const rowYear = new Date(row.start_date).getFullYear();
      if (rowYear !== selectedYear) return sum;

      if (
        editId !== null &&
        (String(row.id) === String(editId) ||
          String(row.target_id) === String(editId))
      ) {
        return sum;
      }

      if (isScheme) {
        if (Number(row.scheme_id) === Number(form.scheme_id))
          return sum + Number(row.target_quantity || 0);
      } else {
        if (row.scheme_type === "Non-Scheme")
          return sum + Number(row.target_quantity || 0);
      }
      return sum;
    }, 0);

    const maxAllowed = totalAllowed - dbUsed;
    return maxAllowed;
  };

  const getDeptRemainingBalance = (lim) => {
    const selectedYear = Number(form.financial_year.split("-")[0]);
    const dbUsed = data.reduce((sum, row) => {
      if (!row.start_date) return sum;
      if (new Date(row.start_date).getFullYear() !== selectedYear) return sum;

      if (
        editId !== null &&
        (String(row.id) === String(editId) ||
          String(row.target_id) === String(editId))
      ) {
        return sum;
      }

      if (lim.scheme_type === "Scheme") {
        if (Number(row.scheme_id) === Number(lim.scheme_id))
          return sum + Number(row.target_quantity || 0);
      } else {
        if (row.scheme_type === "Non-Scheme")
          return sum + Number(row.target_quantity || 0);
      }
      return sum;
    }, 0);
    return Number(lim.target_quantity) - dbUsed;
  };

  // ================= 5. FILTER LOGIC & SORTING =================
  const uniqueFilterSchemes = useMemo(() => {
    const combined = [{ id: "Non-Scheme", name: "Non-Scheme" }, ...schemes];
    return combined.filter(
      (v, i, a) => a.findIndex((t) => String(t.id) === String(v.id)) === i,
    );
  }, [schemes]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    let result = data.filter((row) => {
      const rowDeptId = row.department_id || row.target_department_id;
      const matchDept =
        !filterDepartment || Number(rowDeptId) === Number(filterDepartment.id);
      const matchDist =
        !filterDistrict ||
        Number(row.district_id) === Number(filterDistrict.id);

      let matchScheme = true;
      if (filterScheme) {
        if (String(filterScheme.id) === "Non-Scheme") {
          matchScheme = row.scheme_type === "Non-Scheme";
        } else {
          matchScheme =
            row.scheme_type === "Scheme" &&
            String(row.scheme_id) === String(filterScheme.id);
        }
      }

      return matchDept && matchDist && matchScheme;
    });

    // ✅ SORT TABLE DATA BY DISTRICT NAME (ASCENDING: A, B, C...)
    return result.sort((a, b) => {
      const nameA = (a.district_name || "").toString().toLowerCase();
      const nameB = (b.district_name || "").toString().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [data, filterDepartment, filterDistrict, filterScheme]);

  // ✅ CALCULATE TOTAL TARGET VALUE FOR DISPLAY
  const totalTargetValue = useMemo(() => {
    return filteredData.reduce(
      (sum, row) => sum + Number(row.target_quantity || 0),
      0,
    );
  }, [filteredData]);

  // ================= 6. FORM HELPERS & VALIDATION =================
  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name])
      setErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
  };

  const resetTypeAndScheme = () => {
    setForm((prev) => ({ ...prev, scheme_type: null, scheme_id: null }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n.scheme_type;
      delete n.scheme_id;
      return n;
    });
  };

  const validate = () => {
    const tempErrors = {};
    if (!form.district_id) tempErrors.district_id = "Select a district";
    if (!form.financial_year)
      tempErrors.financial_year = "Select financial year";
    if (!form.scheme_type) tempErrors.scheme_type = "Select a target type";
    if (form.scheme_type === "Scheme" && !form.scheme_id)
      tempErrors.scheme_id = "Select a scheme";
    if (!form.target_quantity || Number(form.target_quantity) <= 0)
      tempErrors.target_quantity = "Enter a valid quantity";

    const max = getMaxAllowed();
    if (
      !tempErrors.target_quantity &&
      max !== null &&
      max < Number(form.target_quantity)
    ) {
      tempErrors.target_quantity =
        max < 0
          ? `Dept limit exceeded by ${Math.abs(max).toLocaleString()}. Delete district targets first.`
          : `Exceeds available limit (${max.toLocaleString()})`;
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // ================= 7. CRUD ACTIONS =================
  const handleSubmit = async () => {
    if (!validate()) return;

    const duplicate = data.find((item) => {
      if (
        editId &&
        (String(item.id) === String(editId) ||
          String(item.target_id) === String(editId))
      )
        return false;
      if (Number(item.district_id) !== Number(form.district_id)) return false;
      const itemYear = item.start_date
        ? `${new Date(item.start_date).getFullYear()}-${new Date(item.start_date).getFullYear() + 1}`
        : null;
      if (itemYear !== form.financial_year) return false;
      if (form.scheme_type === "Scheme") {
        return (
          item.scheme_type === "Scheme" &&
          Number(item.scheme_id) === Number(form.scheme_id)
        );
      }
      return item.scheme_type === "Non-Scheme";
    });

    if (duplicate) {
      const distName =
        districts.find((d) => d.id === form.district_id)?.District_Name ||
        "District";
      const typeName =
        form.scheme_type === "Scheme"
          ? schemes.find((s) => s.id === form.scheme_id)?.name || "Scheme"
          : "Non-Scheme";
      setExistInfo({ district: distName, type: typeName });
      setExistModal(true);
      return;
    }

    try {
      if (editId) {
        await DeleteApi(`${API_URLS.DistrictTarget}/${editId}`, {}, token);
      }

      const fyStart = form.financial_year.split("-")[0];
      const payload = {
        target_department_id: Number(authData?.department_id),
        district_id: Number(form.district_id),
        target_quantity: Number(form.target_quantity),
        start_date: `${fyStart}-04-01`,
        end_date: `${Number(fyStart) + 1}-03-31`,
        created_by: user?.id || 1,
        status: "Active",
        scheme_type: form.scheme_type,
        scheme_id:
          form.scheme_type === "Scheme" ? Number(form.scheme_id) : null,
      };

      await PostApi(`${API_URLS.DistrictTarget}/targetdist/`, payload, token);
      const freshData = await fetchTargets();

      const isComplete = isDistrictComplete(
        form.district_id,
        form.financial_year,
      );

      if (!isComplete) {
        const existing = getExistingForDistYear(
          freshData,
          form.district_id,
          form.financial_year,
        );
        const hasNonScheme = existing.some(
          (t) => t.scheme_type === "Non-Scheme",
        );
        const usedSchemeIds = existing
          .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
          .map((t) => Number(t.scheme_id));
        const hasRemainingSchemes = deptAllowedSchemeIds.some(
          (s) => !usedSchemeIds.includes(s),
        );

        const nextType =
          deptAllowedTypes.includes("Non-Scheme") && !hasNonScheme
            ? "Non-Scheme"
            : deptAllowedTypes.includes("Scheme") && hasRemainingSchemes
              ? "Scheme"
              : null;

        setForm((prev) => ({
          ...prev,
          scheme_type: nextType,
          scheme_id: null,
          target_quantity: "",
        }));
        setErrors({});
        toast.success("Target saved! More targets pending for this district.");
      } else {
        handleClose();
        toast.success("All targets set for this district!");
      }
    } catch (err) {
      console.error("Save Error:", err);
      toast.error(err.response?.data?.message || "Failed to save target");
    }
  };

  const handleEdit = (row) => {
    setEditId(row.id || row.target_id);
    const itemYear = row.start_date
      ? `${new Date(row.start_date).getFullYear()}-${new Date(row.start_date).getFullYear() + 1}`
      : currentFY;
    setForm({
      district_id: row.district_id,
      financial_year: itemYear,
      scheme_type: row.scheme_type || (row.scheme_id ? "Scheme" : "Non-Scheme"),
      scheme_id: row.scheme_id || null,
      target_quantity: row.target_quantity,
    });
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm({
      district_id: null,
      financial_year: currentFY,
      scheme_type: null,
      scheme_id: null,
      target_quantity: "",
    });
    setErrors({});
  };

  const confirmDelete = async () => {
    try {
      await DeleteApi(
        `${API_URLS.DistrictTarget}/${deleteTarget.id || deleteTarget.target_id}`,
        {},
        token,
      );
      toast.success("Target Deleted");
      fetchTargets();
    } catch {
      toast.error("Delete Failed");
    } finally {
      setDeleteModal(false);
    }
  };

  const columns = [
    {
      field: "department_name",
      headerName: "Department",
      render: (r) => r.department_name || userDepartmentName,
    },
    { field: "district_name", headerName: "District" },
    { field: "scheme_type", headerName: "Type" },
    {
      field: "scheme_name",
      headerName: "Scheme",
      render: (r) => r.scheme_name || "—",
    },
    { field: "target_quantity", headerName: "Target Value" },
    {
      field: "start_date",
      headerName: "FY",
      render: (r) =>
        r.start_date
          ? `${new Date(r.start_date).getFullYear()}-${new Date(r.start_date).getFullYear() + 1}`
          : "—",
    },
  ];

  // ================= 8. RENDER =================
  return (
    <Box p={3}>
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 1, border: "1px solid #eee", borderRadius: 2 }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          flexWrap="wrap"
          gap={2}
        >
          <Stack direction="row" spacing={2} flex={1} alignItems="center">
            <FilterListIcon sx={{ color: "#2e7d32" }} />

            {role === "superadmin" && (
              <Autocomplete
                options={departments}
                getOptionLabel={(opt) => opt.name}
                value={filterDepartment}
                onChange={(e, val) => setFilterDepartment(val)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                size="small"
                sx={{ minWidth: 250 }}
                clearIcon={<ClearIcon fontSize="small" />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department"
                    placeholder="Search department..."
                    sx={greenInputSx}
                  />
                )}
              />
            )}

            <Autocomplete
              options={districts}
              getOptionLabel={(opt) => opt.District_Name}
              value={filterDistrict}
              onChange={(e, val) => setFilterDistrict(val)}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              size="small"
              sx={{ minWidth: 250 }}
              clearIcon={<ClearIcon fontSize="small" />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="District"
                  placeholder="Search..."
                  sx={greenInputSx}
                />
              )}
            />

            <Autocomplete
              options={uniqueFilterSchemes}
              getOptionLabel={(opt) => opt.name}
              value={filterScheme}
              onChange={(e, val) => setFilterScheme(val)}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
              size="small"
              sx={{ minWidth: 250 }}
              clearIcon={<ClearIcon fontSize="small" />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Scheme / Type"
                  placeholder="Search..."
                  sx={greenInputSx}
                />
              )}
            />
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            {hasModifyPermission && overallBalance !== 0 && (
              <>
                <Chip
                  icon={<PendingActionsIcon style={{ fontSize: 18 }} />}
                  label={`${overallBalance.toLocaleString()} ${overallBalance < 0 ? "Over-Assigned" : "Pending"}`}
                  color={overallBalance < 0 ? "error" : "warning"}
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    borderColor: overallBalance < 0 ? "red" : "orange",
                    color: overallBalance < 0 ? "#b71c1c" : "#e65100",
                  }}
                />
                {overallBalance > 0 && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => setOpen(true)}
                    sx={{ fontWeight: 600 }}
                  >
                    Set Target
                  </Button>
                )}
              </>
            )}

            {hasModifyPermission && overallBalance === 0 && (
              <Chip
                icon={<TaskAltIcon style={{ fontSize: 18 }} />}
                label="All Targets Completed"
                color="success"
                sx={{
                  fontWeight: 600,
                  bgcolor: "#e8f5e9",
                  color: "#2e7d32",
                  border: "1px solid #a5d6a7",
                }}
              />
            )}
          </Stack>
        </Stack>
      </Paper>

      {hasModifyPermission && pendingDetails.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: pendingDetails.some((d) => d.isOverAssigned)
              ? "1px dashed #ef9a9a"
              : "1px dashed #ffcc80",
            bgcolor: pendingDetails.some((d) => d.isOverAssigned)
              ? "#ffebee"
              : "#fffde7",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            {pendingDetails.some((d) => d.isOverAssigned) ? (
              <ErrorOutlineIcon sx={{ fontSize: 18, color: "#b71c1c" }} />
            ) : (
              <PendingActionsIcon sx={{ fontSize: 18, color: "#e65100" }} />
            )}
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: pendingDetails.some((d) => d.isOverAssigned)
                  ? "#b71c1c"
                  : "#e65100",
              }}
            >
              {pendingDetails.some((d) => d.isOverAssigned)
                ? "Over-Assigned Breakdown"
                : "Pending Breakdown"}{" "}
              ({form.financial_year})
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} useFlexGap>
            {pendingDetails.map((det) => (
              <Chip
                key={det.id}
                icon={
                  det.isOverAssigned ? (
                    <WarningAmberIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <LocalFloristIcon sx={{ fontSize: 16 }} />
                  )
                }
                label={`${det.department_name} - ${det.scheme_name}: ${det.balance.toLocaleString()}`}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: det.isOverAssigned ? "#e57373" : "#ffb74d",
                  bgcolor: "#ffffff",
                  color: det.isOverAssigned ? "#b71c1c" : "#bf360c",
                  fontWeight: 500,
                  "& .MuiChip-icon": {
                    color: det.isOverAssigned ? "#d32f2f" : "#e65100",
                  },
                }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* ✅ TOTAL TARGET VALUE DISPLAY ABOVE TABLE */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid #c8e6c9",
          borderRadius: 2,
          bgcolor: "#f1f8e9",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#2e7d32" }}>
            Total Target Value
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b5e20" }}>
            {totalTargetValue.toLocaleString()}
          </Typography>
        </Stack>
      </Paper>

      <DynamicTable
        data={filteredData}
        columns={columns}
        onEdit={hasModifyPermission ? handleEdit : undefined}
        onDelete={
          hasModifyPermission
            ? (r) => {
                setDeleteTarget(r);
                setDeleteModal(true);
              }
            : undefined
        }
      />

      <Dialog
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DeleteIcon color="error" />
            <Typography fontWeight={600}>Confirm Delete</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this target?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteModal(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={existModal}
        onClose={() => setExistModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningAmberIcon color="warning" />
            <Typography fontWeight={600}>Target Already Exists</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Target for <strong>{existInfo?.district}</strong> with{" "}
            <strong>{existInfo?.type}</strong> is already set for this financial
            year.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExistModal(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <DynamicOffcanvas
        open={open}
        title={editId ? "Edit Target" : "Set District Target"}
        onClose={handleClose}
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                borderColor: "#4caf50",
                color: "#4caf50",
                "&:hover": {
                  borderColor: "#2e7d32",
                  backgroundColor: "rgba(76, 175, 80, 0.08)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                backgroundColor: "#2e7d32",
                "&:hover": { backgroundColor: "#1b5e20" },
              }}
            >
              {editId ? "Update" : "Save Target"}
            </Button>
          </Stack>
        }
      >
        {displayedDeptLimits.length > 0 && (
          <Alert
            severity={
              displayedDeptLimits.some((l) => getDeptRemainingBalance(l) < 0)
                ? "error"
                : "info"
            }
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: 14 }}>
              Dept Targets ({form.financial_year})
            </AlertTitle>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {displayedDeptLimits.map((lim) => {
                const bal = getDeptRemainingBalance(lim);
                return (
                  <Typography
                    key={lim.target_id}
                    variant="body2"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      color: bal < 0 ? "error.main" : "text.primary",
                    }}
                  >
                    <span>
                      {lim.scheme_type === "Scheme"
                        ? lim.scheme_name
                        : "Non-Scheme"}
                      :
                    </span>
                    <strong>
                      {bal.toLocaleString()}{" "}
                      {bal < 0 ? "(Over-assigned)" : "left"}
                    </strong>
                  </Typography>
                );
              })}
              <Divider sx={{ my: 0.5 }} />
              <Typography
                variant="body2"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  color:
                    displayedDeptLimits.reduce(
                      (acc, curr) => acc + getDeptRemainingBalance(curr),
                      0,
                    ) < 0
                      ? "error.main"
                      : "text.primary",
                }}
              >
                <span>Overall Remaining:</span>
                <strong>
                  {displayedDeptLimits
                    .reduce(
                      (acc, curr) => acc + getDeptRemainingBalance(curr),
                      0,
                    )
                    .toLocaleString()}
                </strong>
              </Typography>
            </Stack>
          </Alert>
        )}

        {hierarchyStatus && (
          <Alert
            severity="success"
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: 14 }}>
              District Allocation Progress
            </AlertTitle>
            <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
              {deptAllowedTypes.includes("Non-Scheme") && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {hierarchyStatus.nonSchemeSet ? (
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  ) : (
                    <RadioButtonUncheckedIcon
                      fontSize="small"
                      color="disabled"
                    />
                  )}
                  <Typography variant="body2">Non-Scheme</Typography>
                </Stack>
              )}
              {deptAllowedTypes.includes("Scheme") && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {hierarchyStatus.remainingSchemes === 0 ? (
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  ) : (
                    <RadioButtonUncheckedIcon
                      fontSize="small"
                      color="disabled"
                    />
                  )}
                  <Typography variant="body2">
                    Schemes ({hierarchyStatus.schemesSet}/
                    {hierarchyStatus.totalSchemes})
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Alert>
        )}

        <Grid container spacing={2.5}>
          <Grid item xs={12} width="250px">
            <TextField
              fullWidth
              size="small"
              label="Department"
              value={userDepartmentName}
              InputProps={{ readOnly: true }}
              sx={greenInputSx}
            />
          </Grid>

          <Grid item xs={12} width="250px">
            <Autocomplete
              options={years}
              getOptionLabel={(opt) => opt}
              value={form.financial_year || null}
              onChange={(e, newValue) => {
                handleChange("financial_year", newValue || null);
                resetTypeAndScheme();
              }}
              isOptionEqualToValue={(o, v) => o === v}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Financial Year"
                  placeholder="Select year..."
                  error={!!errors.financial_year}
                  helperText={errors.financial_year}
                  sx={greenInputSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} width="250px">
            <Autocomplete
              options={availableDistricts}
              getOptionLabel={(opt) => opt.District_Name}
              value={districts.find((d) => d.id === form.district_id) || null}
              onChange={(e, newValue) => {
                handleChange("district_id", newValue?.id || null);
                resetTypeAndScheme();
              }}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              size="small"
              disabled={!form.financial_year}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="District"
                  placeholder="Search district..."
                  error={!!errors.district_id}
                  helperText={
                    errors.district_id ||
                    (!form.financial_year
                      ? "Select year first"
                      : `${availableDistricts.length} district(s) available`)
                  }
                  sx={greenInputSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} width="250px">
            <Autocomplete
              options={availableTypes}
              getOptionLabel={(opt) => opt}
              value={form.scheme_type || null}
              onChange={(e, newValue) => {
                handleChange("scheme_type", newValue || null);
                handleChange("scheme_id", null);
              }}
              isOptionEqualToValue={(o, v) => o === v}
              size="small"
              disabled={!form.district_id || !form.financial_year}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Type"
                  placeholder="Select type..."
                  error={!!errors.scheme_type}
                  helperText={
                    errors.scheme_type ||
                    (!form.district_id ? "Select district first" : "")
                  }
                  sx={greenInputSx}
                />
              )}
            />
          </Grid>

          {form.scheme_type === "Scheme" && (
            <Grid item xs={12} width="250px">
              <Autocomplete
                options={availableSchemes}
                getOptionLabel={(opt) => opt.name}
                value={
                  schemes.find(
                    (s) => Number(s.id) === Number(form.scheme_id),
                  ) || null
                }
                onChange={(e, newValue) =>
                  handleChange("scheme_id", newValue?.id || null)
                }
                isOptionEqualToValue={(o, v) => Number(o.id) === Number(v?.id)}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Scheme"
                    placeholder="Search scheme..."
                    error={!!errors.scheme_id}
                    helperText={
                      errors.scheme_id ||
                      `${availableSchemes.length} scheme(s) remaining`
                    }
                    sx={greenInputSx}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} width="250px">
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Target Quantity"
              placeholder="Enter quantity..."
              value={form.target_quantity}
              onChange={(e) => {
                let val = e.target.value;
                val = val.replace(/[eE+-]/g, "");
                if (val === "-") val = "";

                handleChange("target_quantity", val);

                const maxLimit = getMaxAllowed();
                if (maxLimit !== null && val !== "" && Number(val) > maxLimit) {
                  setErrors((prev) => ({
                    ...prev,
                    target_quantity:
                      maxLimit < 0
                        ? `Dept limit exceeded by ${Math.abs(maxLimit).toLocaleString()}. Delete district targets first.`
                        : `Max limit is ${maxLimit.toLocaleString()}`,
                  }));
                } else if (
                  errors.target_quantity &&
                  (val === "" || Number(val) <= (maxLimit || 0))
                ) {
                  setErrors((prev) => {
                    const n = { ...prev };
                    delete n.target_quantity;
                    return n;
                  });
                }
              }}
              inputProps={{ min: 0 }}
              error={!!errors.target_quantity}
              helperText={
                errors.target_quantity ||
                (getMaxAllowed() !== null
                  ? getMaxAllowed() < 0
                    ? `Limit exceeded by ${Math.abs(getMaxAllowed()).toLocaleString()}`
                    : `Max Limit: ${getMaxAllowed().toLocaleString()}`
                  : "Select scheme/type to see limit")
              }
              sx={greenInputSx}
            />
          </Grid>
        </Grid>
      </DynamicOffcanvas>
    </Box>
  );
};

export default DistrictTarget;
